import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getShowPositions, updatePositionsData } from "@/Api/positions";
import { getSelectSectors } from "@/Api/sectors";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin, CreateLoaderInput } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { UnitySelectField } from "@/components/UnitySelectField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const schema = z.object({
  unity_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  description: z.string().optional(),
  sector_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
});

type SchemaType = z.infer<typeof schema>;

export const UpdateBtn = ({
  updateFront,
  uuid,
}: {
  updateFront: (response: any) => void;
  uuid: string;
}) => {
  const [sectors, setSectors] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const unityId = form.watch("unity_id");

  useEffect(() => {
    if (!isOpen || !unityId) {
      setSectors([]);
      return;
    }
    getSelectSectors(unityId).then(({ data }) =>
      setSectors(data?.response || [])
    );
  }, [isOpen, unityId]);

  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        setIsLoading(true);
        try {
          const { data: responseApi } = await getShowPositions(uuid);

          form.setValue(
            "unity_id",
            responseApi?.response?.unity_id != null
              ? String(responseApi.response.unity_id)
              : ""
          );
          form.setValue("name", responseApi?.response?.name || "");
          form.setValue(
            "description",
            responseApi?.response?.description || ""
          );
          form.setValue(
            "sector_id",
            String(responseApi?.response?.sector_id)
          );
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [isOpen, uuid, form]);

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");

    const { data: apiResponse, ok } = await updatePositionsData(uuid, data);
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }

    updateFront(apiResponse?.response);
    setFormState("default");
    setIsOpen(false);
    toast({
      description: apiResponse?.message,
      action: <Check color="#1edc2b" />,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={() => setIsOpen(true)}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Cargo
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <CreateLoaderInput lines={5} />
        ) : (
          <Form
            onSubmit={form.handleSubmit(formSubmit)}
            {...form}
            className="space-y-3"
          >
            <Form.Row>
              <UnitySelectField
                onChange={() => form.setValue("sector_id", "")}
              />
            </Form.Row>

            <Form.Row>
              <Form.Field name="name">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Nome <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Insira um nome" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <Form.Row>
              <Form.Field name="sector_id">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Setor <Form.Message />
                    </Form.Label>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={!unityId}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            unityId
                              ? "Selecione um setor"
                              : "Selecione uma unidade primeiro"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {sectors.map((sector: { id: string; name: string }) => (
                          <SelectItem
                            key={sector.id}
                            value={sector.id.toString()}
                          >
                            {sector.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <Form.Row>
              <Form.Textarea name="description" placeholder="Insira uma descrição (opcional)">
                Descrição
              </Form.Textarea>
            </Form.Row>

            <DialogFooter>
              <Button
                type="submit"
                className={cn("w-full", colors.bg_primary)}
                disabled={formState !== "default"}
              >
                {formState === "loading" && (
                  <>
                    Atualizando <LoaderSpin />
                  </>
                )}
                {formState === "default" && "Atualizar"}
                {formState === "error" && "Erro ao atualizar"}
              </Button>
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
